import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Bot, Save, Info, Zap, Crown, Cpu, Sparkles, Users, Star } from 'lucide-react';
import ErnieProPricingSettings from './ErnieProPricingSettings';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Provider icons and colors
const PROVIDER_CONFIG = {
  openai: {
    name: 'OpenAI',
    color: 'from-green-500 to-emerald-600',
    bgColor: 'bg-green-500/10',
    borderColor: 'border-green-500',
    textColor: 'text-green-400'
  },
  anthropic: {
    name: 'Anthropic',
    color: 'from-orange-500 to-amber-600',
    bgColor: 'bg-orange-500/10',
    borderColor: 'border-orange-500',
    textColor: 'text-orange-400'
  },
  gemini: {
    name: 'Google',
    color: 'from-blue-500 to-indigo-600',
    bgColor: 'bg-blue-500/10',
    borderColor: 'border-blue-500',
    textColor: 'text-blue-400'
  }
};

export default function ErnieAiSettings() {
  const [settings, setSettings] = useState(null);
  const [freeModel, setFreeModel] = useState('gpt-4o-mini');
  const [proModel, setProModel] = useState('gpt-5');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('models'); // 'models' or 'pricing'

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await axios.get(`${API}/admin/ernie-ai-settings`, {
        withCredentials: true
      });
      setSettings(response.data);
      setFreeModel(response.data.free_model || 'gpt-4o-mini');
      setProModel(response.data.pro_model || 'gpt-5');
    } catch (error) {
      console.error('Failed to load Ernie AI settings:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await axios.put(
        `${API}/admin/ernie-ai-settings`,
        { 
          free_model: freeModel,
          pro_model: proModel 
        },
        { withCredentials: true }
      );
      setSettings(response.data);
      toast.success('Model settings saved!');
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  // Group models by provider
  const getModelsByProvider = () => {
    if (!settings?.available_models) return {};
    
    const grouped = {};
    Object.entries(settings.available_models).forEach(([key, model]) => {
      const provider = model.provider;
      if (!grouped[provider]) {
        grouped[provider] = [];
      }
      grouped[provider].push({ key, ...model });
    });
    
    return grouped;
  };

  if (loading) {
    return (
      <div className="admin-card">
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#E8934D]"></div>
        </div>
      </div>
    );
  }

  if (!settings) return null;

  const hasChanges = freeModel !== settings.free_model || proModel !== settings.pro_model;
  const modelsByProvider = getModelsByProvider();

  // Render model option card
  const ModelOption = ({ model, isSelected, onClick, tierLabel }) => {
    const config = PROVIDER_CONFIG[model.provider] || PROVIDER_CONFIG.openai;
    return (
      <div
        onClick={onClick}
        className={`p-4 rounded-lg border-2 transition-all cursor-pointer hover:border-[#E8934D]/50 ${
          isSelected
            ? `${config.borderColor} ${config.bgColor}`
            : 'border-gray-600 bg-gray-800/30 hover:bg-gray-800/50'
        }`}
        data-testid={`model-option-${model.key}-${tierLabel}`}
      >
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <p className="font-bold text-white flex items-center gap-2 flex-wrap">
              {model.name}
              {isSelected && (
                <span className={`text-xs ${tierLabel === 'free' ? 'bg-blue-500' : 'bg-purple-500'} text-white px-2 py-1 rounded`}>
                  {tierLabel === 'free' ? 'FREE MODEL' : 'PRO MODEL'}
                </span>
              )}
            </p>
            <p className="text-sm text-gray-300 mt-1">{model.description}</p>
            {model.supports_images && (
              <p className="text-xs text-green-400 mt-1 flex items-center gap-1">
                <Sparkles className="w-3 h-3" />
                Supports images
              </p>
            )}
          </div>
          <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
            isSelected 
              ? `${config.borderColor} ${config.bgColor}` 
              : 'border-gray-500'
          }`}>
            {isSelected && (
              <div className={`w-3 h-3 rounded-full bg-gradient-to-r ${config.color}`}></div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6" data-testid="ernie-ai-settings">
      {/* Tab Selector */}
      <div className="flex gap-4 border-b-2 border-[#8B6F47]">
        <button
          onClick={() => setActiveTab('models')}
          className={`px-6 py-3 font-bold transition-all ${
            activeTab === 'models'
              ? 'border-b-4 border-[#E8934D] text-[#E8934D]'
              : 'text-gray-400 hover:text-gray-300'
          }`}
          data-testid="tab-models"
        >
          <Bot className="w-5 h-5 inline mr-2" />
          AI Models
        </button>
        <button
          onClick={() => setActiveTab('pricing')}
          className={`px-6 py-3 font-bold transition-all ${
            activeTab === 'pricing'
              ? 'border-b-4 border-[#E8934D] text-[#E8934D]'
              : 'text-gray-400 hover:text-gray-300'
          }`}
          data-testid="tab-pricing"
        >
          <Crown className="w-5 h-5 inline mr-2" />
          Pro Subscription
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === 'pricing' ? (
        <ErnieProPricingSettings />
      ) : (
        <div className="space-y-8">
          {/* Header */}
          <div className="admin-card">
            <div className="flex items-center gap-3 mb-4">
              <Bot className="w-8 h-8 text-[#E8934D]" />
              <div>
                <h2 className="text-2xl font-bold text-white">Ernie AI Model Configuration</h2>
                <p className="text-sm text-gray-300">Set different AI models for Free and Pro users</p>
              </div>
            </div>

            {/* Important Note */}
            <div className="p-4 bg-blue-900/50 border-2 border-blue-500 rounded-lg">
              <div className="flex items-start gap-3">
                <Info className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-blue-100">
                  <p className="font-bold mb-1">Separate Models for Each Tier:</p>
                  <p>Free users will use the <strong>Free Model</strong> (typically a faster, cost-effective option). Pro subscribers will use the <strong>Pro Model</strong> (typically more capable). Users always see &ldquo;Ernie AI&rdquo; regardless of which model powers their experience.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Two-Column Model Selection */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* FREE MODEL SELECTION */}
            <div className="admin-card">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-blue-500/20 rounded-lg">
                  <Users className="w-6 h-6 text-blue-400" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">Free Tier Model</h3>
                  <p className="text-sm text-gray-400">For users without Pro subscription</p>
                </div>
              </div>

              {/* Current Selection */}
              <div className="mb-4 p-3 bg-blue-900/30 border-2 border-blue-500 rounded-lg">
                <p className="text-blue-100 text-sm flex items-center gap-2">
                  <Zap className="w-4 h-4" />
                  <strong>Current:</strong> {settings.available_models[freeModel]?.name || freeModel}
                </p>
              </div>

              {/* Model Options */}
              <div className="space-y-3 max-h-96 overflow-y-auto pr-2">
                {Object.entries(modelsByProvider).map(([provider, models]) => {
                  const config = PROVIDER_CONFIG[provider] || PROVIDER_CONFIG.openai;
                  return (
                    <div key={`free-${provider}`} className="space-y-2">
                      <h4 className={`text-sm font-bold ${config.textColor} flex items-center gap-2`}>
                        <Cpu className="w-4 h-4" />
                        {config.name}
                      </h4>
                      {models.map((model) => (
                        <ModelOption 
                          key={`free-${model.key}`}
                          model={model}
                          isSelected={freeModel === model.key}
                          onClick={() => setFreeModel(model.key)}
                          tierLabel="free"
                        />
                      ))}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* PRO MODEL SELECTION */}
            <div className="admin-card">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-purple-500/20 rounded-lg">
                  <Star className="w-6 h-6 text-purple-400" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">Pro Tier Model</h3>
                  <p className="text-sm text-gray-400">For Ernie AI Pro subscribers</p>
                </div>
              </div>

              {/* Current Selection */}
              <div className="mb-4 p-3 bg-purple-900/30 border-2 border-purple-500 rounded-lg">
                <p className="text-purple-100 text-sm flex items-center gap-2">
                  <Crown className="w-4 h-4" />
                  <strong>Current:</strong> {settings.available_models[proModel]?.name || proModel}
                </p>
              </div>

              {/* Model Options */}
              <div className="space-y-3 max-h-96 overflow-y-auto pr-2">
                {Object.entries(modelsByProvider).map(([provider, models]) => {
                  const config = PROVIDER_CONFIG[provider] || PROVIDER_CONFIG.openai;
                  return (
                    <div key={`pro-${provider}`} className="space-y-2">
                      <h4 className={`text-sm font-bold ${config.textColor} flex items-center gap-2`}>
                        <Cpu className="w-4 h-4" />
                        {config.name}
                      </h4>
                      {models.map((model) => (
                        <ModelOption 
                          key={`pro-${model.key}`}
                          model={model}
                          isSelected={proModel === model.key}
                          onClick={() => setProModel(model.key)}
                          tierLabel="pro"
                        />
                      ))}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Auto-Switch Note */}
          <div className="admin-card">
            <div className="p-4 bg-purple-900/30 border-2 border-purple-500 rounded-lg">
              <div className="flex items-start gap-3">
                <Info className="w-5 h-5 text-purple-400 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-purple-100">
                  <p className="font-bold mb-1">Automatic Image Handling:</p>
                  <p>When users upload images, Ernie automatically switches to a Gemini model that supports multimodal content, regardless of the configured tier model.</p>
                </div>
              </div>
            </div>

            {/* Save Button */}
            <div className="mt-6 flex items-center gap-4">
              <button
                onClick={handleSave}
                disabled={saving || !hasChanges}
                className="flex-1 retro-button flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="save-model-button"
              >
                <Save className="w-5 h-5" />
                {saving ? 'Saving...' : hasChanges ? 'Save Model Settings' : 'No Changes'}
              </button>
              
              {hasChanges && (
                <button
                  onClick={() => {
                    setFreeModel(settings.free_model || 'gpt-4o-mini');
                    setProModel(settings.pro_model || 'gpt-5');
                  }}
                  className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
                  data-testid="cancel-button"
                >
                  Cancel
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
